import { useState } from "react";
import {
  FileText,
  Plus,
  Send,
  FileCheck,
  Clock,
  CheckCircle2,
  FileEdit,
  LayoutTemplate,
  ArrowRight,
  MoreHorizontal,
  Eye,
  Trash2,
  Copy,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import DashboardCharts from "@/components/DashboardCharts";
import { Badge } from "@/components/ui/badge";
import {
  ActionSheet,
  ActionSheetContent,
  ActionSheetItem,
  ActionSheetSeparator,
} from "@/components/ui/bottom-sheet";
import { Button, FloatingActionButton } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  HorizontalScroll,
  HorizontalScrollItem,
} from "@/components/ui/responsive-table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { useDashboardData, useTemplatesForPicker } from "@/hooks/useQueries";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort } from "@/utils/dateFormat";

const StatCard = ({ title, value, description, icon: Icon }) => (
  <Card className="card-pressable">
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">
        {title}
      </CardTitle>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {description && (
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      )}
    </CardContent>
  </Card>
);

const QuickActionCard = ({
  title,
  description,
  icon: Icon,
  onClick,
  variant = "default",
}) => {
  const iconColors = {
    default: "bg-primary/10 text-primary",
    purple: "bg-purple-500 text-white",
    green: "bg-emerald-500 text-white",
  };
  return (
    <Card
      className="group cursor-pointer card-pressable transition-all hover:shadow-md hover:border-primary/50"
      onClick={onClick}
    >
      <CardContent className="flex items-center gap-4 p-4">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconColors[variant]}`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium leading-none mb-1">{title}</h3>
          <p className="text-sm text-muted-foreground line-clamp-1">
            {description}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const statusConfig = {
    draft: {
      label: t("envelopes.status.draft"),
      className: "bg-slate-500 text-white",
    },
    sent: {
      label: t("envelopes.status.sent"),
      className: "bg-blue-500 text-white",
    },
    viewed: {
      label: t("envelopes.status.viewed"),
      className: "bg-sky-500 text-white",
    },
    partially_signed: {
      label: t("envelopes.status.partially_signed"),
      className: "bg-amber-500 text-white",
    },
    completed: {
      label: t("envelopes.status.completed"),
      className: "bg-emerald-500 text-white",
    },
    declined: {
      label: t("envelopes.status.declined"),
      className: "bg-red-500 text-white",
    },
    voided: {
      label: t("envelopes.status.voided"),
      className: "bg-rose-500 text-white",
    },
    expired: {
      label: t("envelopes.status.expired"),
      className: "bg-orange-500 text-white",
    },
  };
  const config = statusConfig[status] || statusConfig.draft;
  return (
    <Badge className={`${config.className} border-0 font-medium`}>
      {config.label}
    </Badge>
  );
};

const EnvelopeListItem = ({ envelope }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuthStore.getState();
  const [actionSheetOpen, setActionSheetOpen] = useState(false);

  const handleMoreClick = (e) => {
    e.stopPropagation();
    if (isMobile) setActionSheetOpen(true);
  };

  return (
    <>
      <div
        className="flex items-center gap-4 p-4 hover:bg-muted/50 active:bg-muted/70 transition-colors cursor-pointer"
        onClick={() => navigate(`/envelopes/${envelope.id}`)}
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <FileText className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{envelope.title}</p>
          <p className="text-sm text-muted-foreground">
            {envelope.recipientCount} recipient
            {envelope.recipientCount !== 1 ? "s" : ""} ·{" "}
            {envelope.documentCount} document
            {envelope.documentCount !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={envelope.status} />
          <span className="text-sm text-muted-foreground whitespace-nowrap hidden sm:inline">
            {formatDateShort(envelope.updatedAt, user)}
          </span>
          {isMobile ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 touch-target"
              onClick={handleMoreClick}
            >
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/envelopes/${envelope.id}`);
                  }}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  {t("common.view")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                  <Copy className="mr-2 h-4 w-4" />
                  {t("common.copy")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => e.stopPropagation()}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {isMobile && (
        <ActionSheet open={actionSheetOpen} onOpenChange={setActionSheetOpen}>
          <ActionSheetContent title={envelope.title}>
            <ActionSheetItem
              onClick={() => {
                setActionSheetOpen(false);
                navigate(`/envelopes/${envelope.id}`);
              }}
            >
              <Eye className="h-5 w-5" />
              {t("common.view")}
            </ActionSheetItem>
            <ActionSheetItem onClick={() => setActionSheetOpen(false)}>
              <Copy className="h-5 w-5" />
              {t("common.copy")}
            </ActionSheetItem>
            <ActionSheetSeparator />
            <ActionSheetItem
              destructive
              onClick={() => setActionSheetOpen(false)}
            >
              <Trash2 className="h-5 w-5" />
              {t("common.delete")}
            </ActionSheetItem>
          </ActionSheetContent>
        </ActionSheet>
      )}
    </>
  );
};

const DashboardSkeleton = () => {
  const isMobile = useIsMobile();
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        {!isMobile && <Skeleton className="h-10 w-36" />}
      </div>
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

const EmptyState = ({ onNewEnvelope }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
        <FileText className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-1">
        {t("dashboard.noEnvelopes")}
      </h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-sm">
        {t("dashboard.noEnvelopesDesc")}
      </p>
      <Button onClick={onNewEnvelope} size="lg" className="touch-target">
        <Plus className="mr-2 h-4 w-4" />
        {t("envelopes.newEnvelope")}
      </Button>
    </div>
  );
};

const TemplatePickerDialog = ({
  open,
  onOpenChange,
  templates,
  loading,
  onSelect,
  onManageTemplates,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const renderDialogContent = () =>
    templates.length === 0 ? (
      <div className="py-8 text-center text-muted-foreground">
        <LayoutTemplate className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t("templates.noTemplates")}</p>
      </div>
    ) : (
      <ScrollArea className="max-h-75 sm:max-h-100">
        <div className="space-y-2 pr-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted active:bg-muted/70 cursor-pointer transition-colors"
              onClick={() => onSelect(template)}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <LayoutTemplate className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{template.name}</p>
                {template.description && (
                  <p className="text-sm text-muted-foreground truncate">
                    {template.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isMobile ? "max-h-[80vh]" : ""}>
        <DialogHeader>
          <DialogTitle>{t("templates.selectTemplate")}</DialogTitle>
          <DialogDescription>
            {t("templates.selectTemplateDesc")}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="space-y-2 py-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : (
          renderDialogContent()
        )}
        <Separator />
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={onManageTemplates}
            size={isMobile ? "mobile" : "default"}
          >
            {t("templates.title")}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            size={isMobile ? "mobile" : "default"}
          >
            {t("common.cancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const { data, isLoading } = useDashboardData();
  const stats = data?.stats;
  const recentEnvelopes = data?.recentEnvelopes || [];
  const recentActivities = data?.recentActivities || [];

  const {
    data: templates = [],
    isLoading: templatesLoading,
    refetch: fetchTemplates,
  } = useTemplatesForPicker();

  const handleNewEnvelope = () => navigate("/envelopes/new");
  const handleUseTemplate = () => {
    setShowTemplateModal(true);
    fetchTemplates();
  };
  const handleSelectTemplate = (template) => {
    setShowTemplateModal(false);
    navigate("/envelopes/new", { state: { templateId: template.id } });
  };
  const handleSignYourself = () =>
    navigate("/envelopes/new", { state: { signYourself: true } });
  const handleManageTemplates = () => {
    setShowTemplateModal(false);
    navigate("/templates");
  };

  const renderActivities = (activity) => {
    if (
      activity?.type?.includes("signed") ||
      activity?.type?.includes("completed")
    ) {
      return <FileCheck className="h-5 w-5 text-emerald-500" />;
    }
    if (activity?.type?.includes("sent")) {
      return <Send className="h-5 w-5 text-blue-500" />;
    }
    return activity?.type?.includes("viewed") ? (
      <Eye className="h-5 w-5 text-slate-500" />
    ) : (
      <FileText className="h-5 w-5 text-primary" />
    );
  };

  if (isLoading) return <DashboardSkeleton />;

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {user?.firstName
              ? t("dashboard.welcome", { name: user.firstName })
              : t("dashboard.welcomeDefault")}
          </h1>
          <p className="text-muted-foreground">{t("dashboard.subtitle")}</p>
        </div>
        {!isMobile && (
          <Button onClick={handleNewEnvelope}>
            <Plus className="mr-2 h-4 w-4" />
            {t("envelopes.newEnvelope")}
          </Button>
        )}
      </div>

      {/* Stats - horizontal scroll on mobile */}
      {isMobile ? (
        <HorizontalScroll>
          <HorizontalScrollItem className="w-40">
            <StatCard
              title={t("dashboard.stats.pendingSignatures")}
              value={stats?.actionRequired || 0}
              icon={Clock}
            />
          </HorizontalScrollItem>
          <HorizontalScrollItem className="w-40">
            <StatCard
              title={t("dashboard.stats.completed")}
              value={stats?.completed || 0}
              icon={CheckCircle2}
            />
          </HorizontalScrollItem>
          <HorizontalScrollItem className="w-40">
            <StatCard
              title={t("dashboard.stats.drafts")}
              value={stats?.draft || 0}
              icon={FileEdit}
            />
          </HorizontalScrollItem>
          <HorizontalScrollItem className="w-40">
            <StatCard
              title={t("dashboard.stats.templates")}
              value={stats?.templates || 0}
              icon={LayoutTemplate}
            />
          </HorizontalScrollItem>
        </HorizontalScroll>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title={t("dashboard.stats.pendingSignatures")}
            value={stats?.actionRequired || 0}
            icon={Clock}
            description="Awaiting action"
          />
          <StatCard
            title={t("dashboard.stats.completed")}
            value={stats?.completed || 0}
            icon={CheckCircle2}
            description="This month"
          />
          <StatCard
            title={t("dashboard.stats.drafts")}
            value={stats?.draft || 0}
            icon={FileEdit}
            description="In progress"
          />
          <StatCard
            title={t("dashboard.stats.templates")}
            value={stats?.templates || 0}
            icon={LayoutTemplate}
            description="Available"
          />
        </div>
      )}

      <DashboardCharts stats={stats} monthlyData={stats?.monthlyData} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t("dashboard.recentEnvelopes")}</CardTitle>
            <CardDescription>{t("dashboard.recentEnvelopes")}</CardDescription>
          </div>
          <Link
            to="/envelopes"
            className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("dashboard.viewAll")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {recentEnvelopes.length === 0 ? (
            <EmptyState onNewEnvelope={handleNewEnvelope} />
          ) : (
            <div className="divide-y">
              {recentEnvelopes.map((envelope) => (
                <EnvelopeListItem key={envelope.id} envelope={envelope} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick actions - horizontal scroll on mobile */}
      <div>
        <h2 className="text-lg font-semibold mb-4">
          {t("dashboard.actions.title")}
        </h2>
        {isMobile ? (
          <HorizontalScroll>
            <HorizontalScrollItem className="w-70">
              <QuickActionCard
                title={t("dashboard.actions.sendForSignature")}
                description={t("dashboard.actions.sendForSignatureDesc")}
                icon={Send}
                onClick={handleNewEnvelope}
                variant="default"
              />
            </HorizontalScrollItem>
            <HorizontalScrollItem className="w-70">
              <QuickActionCard
                title={t("dashboard.actions.useTemplate")}
                description={t("dashboard.actions.useTemplateDesc")}
                icon={LayoutTemplate}
                onClick={handleUseTemplate}
                variant="purple"
              />
            </HorizontalScrollItem>
            <HorizontalScrollItem className="w-70">
              <QuickActionCard
                title={t("dashboard.actions.signYourself")}
                description={t("dashboard.actions.signYourselfDesc")}
                icon={FileCheck}
                onClick={handleSignYourself}
                variant="green"
              />
            </HorizontalScrollItem>
          </HorizontalScroll>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            <QuickActionCard
              title={t("dashboard.actions.sendForSignature")}
              description={t("dashboard.actions.sendForSignatureDesc")}
              icon={Send}
              onClick={handleNewEnvelope}
              variant="default"
            />
            <QuickActionCard
              title={t("dashboard.actions.useTemplate")}
              description={t("dashboard.actions.useTemplateDesc")}
              icon={LayoutTemplate}
              onClick={handleUseTemplate}
              variant="purple"
            />
            <QuickActionCard
              title={t("dashboard.actions.signYourself")}
              description={t("dashboard.actions.signYourselfDesc")}
              icon={FileCheck}
              onClick={handleSignYourself}
              variant="green"
            />
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t("dashboard.recentActivity")}</CardTitle>
            <CardDescription>
              {t("dashboard.recentActivityDesc")}
            </CardDescription>
          </div>
          <Link
            to="/activity"
            className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("dashboard.seeAll")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {recentActivities.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t("dashboard.noRecentActivity")}</p>
            </div>
          ) : (
            <div className="divide-y">
              {recentActivities.map((activity, index) => (
                <div
                  key={activity.id || index}
                  className="flex items-center gap-4 p-4 hover:bg-muted/50 active:bg-muted/70 transition-colors cursor-pointer"
                  onClick={() =>
                    activity.entityType === "envelope" &&
                    activity.entityId &&
                    navigate(`/envelopes/${activity.entityId}`)
                  }
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    {renderActivities(activity)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {activity.type
                        ?.replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase()) || "Activity"}
                    </p>
                    {activity.entityName && (
                      <p className="text-sm text-muted-foreground truncate">
                        {activity.entityName}
                      </p>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground whitespace-nowrap hidden sm:inline">
                    {activity.date ? formatDateShort(activity.date, user) : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {isMobile && (
        <FloatingActionButton onClick={handleNewEnvelope}>
          <Plus className="h-6 w-6" />
        </FloatingActionButton>
      )}

      <TemplatePickerDialog
        open={showTemplateModal}
        onOpenChange={setShowTemplateModal}
        templates={templates}
        loading={templatesLoading}
        onSelect={handleSelectTemplate}
        onManageTemplates={handleManageTemplates}
      />
    </div>
  );
};

export default Dashboard;
